import { useState, Fragment, useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import Slider from '@mui/material/Slider';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import { data_vis_parameters, state } from '../App';
import { raiseEvent } from '../util/Events';

const PFACTOR_MIN = 0.0;
const PFACTOR_MAX = 2.0;
const marks_pfactor = [
    {
        value: PFACTOR_MIN,
        label: '',
    },
    {
        value: PFACTOR_MAX,
        label: '',
    },
];

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
}));

export const DistanceUnits = {
    PARSECS: 0,
    LIGHT_YEARS: 1,
    AU: 2,
}

export const ColorByModes = {
    WHITE: 0,
    DISTANCE_HUES: 1,
    DISTANCE_GRAYSCALE: 2,
    ST_SPECTYPE: 3
}

function StandardSlider({ name, value, min, max, handleChange, step = .01, valueLabelDisplay = "auto", labelSuffix = "", ariaLabel = "", extraMarks = [] }) {
    const marks = [
        {
            value: min,
            label: '',
        },
        {
            value: max,
            label: '',
        },
    ]
    extraMarks.forEach((value) => {
        marks.push({
            value: value,
            label: ''
        })
    });

    return (<Fragment>
        {name}
        <Slider
            marks={marks}
            getAriaLabel={() => ariaLabel || name}
            value={value}
            min={min}
            max={max}
            onChange={handleChange}
            valueLabelDisplay={valueLabelDisplay}
            step={step}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
                variant="body2"
                sx={{ cursor: 'pointer' }}
            >
                {min}{labelSuffix}
            </Typography>
            <Typography
                variant="body2"
                sx={{ cursor: 'pointer' }}
            >
                {max}{labelSuffix}
            </Typography>
        </Box>
    </Fragment>);
}

function DrawerList({ handleDrawerClose }) {
    const DIST_RANGE = [0, 8240]

    const theme = useTheme();
    const [distUnitChosen, setDistUnitChosen] = useState(DistanceUnits.PARSECS);
    const [colorByModeChosen, setColorByModeChosen] = useState(ColorByModes.ST_SPECTYPE);
    const [distRangeValue, setDistRangeValue] = useState(DIST_RANGE);
    const [perspectiveFactorValue, setPerspectiveFactorValue] = useState(.6);
    const [starScaleValue, setStarScaleValue] = useState(.3);

    const handleChange_dist = (event, newValue) => {
        setDistRangeValue(newValue);
        data_vis_parameters.filters.distance = newValue; //[min,max]
    };

    const handleChange_pfactor = (event, newValue) => {
        setPerspectiveFactorValue(newValue);
        state.perspectiveFactor = newValue;
    };

    const handleChange_starScale = (event, newValue) => {
        setStarScaleValue(newValue);
        state.starScale = newValue;
    };

    const handleChange_dist_units = (event) => {
        setDistUnitChosen(event.target.value);
        state.distanceUnitsChosen = event.target.value;
    };

    const handleChange_colorByMode = (event) => {
        setColorByModeChosen(event.target.value);
        state.colorByModeChosen = event.target.value;
    }

    const onClick_Legend = () => {
        raiseEvent('openLegend')
    }

    useEffect(() => {
        data_vis_parameters.filters.distance = distRangeValue;
        state.perspectiveFactor = perspectiveFactorValue;
        state.starScale = starScaleValue;
        state.distanceUnitsChosen = distUnitChosen;
        state.colorByModeChosen = colorByModeChosen;
    });

    return (
        <Box sx={{ width: 500 }} role="presentation">
            <DrawerHeader>
                <b style={{ position: "relative", left: "10px" }}>(Press [TAB] to close)</b>
                <IconButton onClick={handleDrawerClose}>
                    {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            </DrawerHeader>
            <Divider />
            <Box display='flex' justifyContent='left' padding={1} gap={1}>
                <Button variant='contained' onClick={onClick_Legend}>Legend</Button>
            </Box>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    Units
                </AccordionSummary>
                <AccordionDetails>
                    <FormControl fullWidth>
                        <InputLabel >Distance</InputLabel>
                        <Select
                            value={distUnitChosen}
                            label="Distance"
                            onChange={handleChange_dist_units}
                        >
                            <MenuItem value={DistanceUnits.PARSECS}>Parsecs (pc)</MenuItem>
                            <MenuItem value={DistanceUnits.LIGHT_YEARS}>Light Years (lyr)</MenuItem>
                            <MenuItem value={DistanceUnits.AU}>Astronomical Units (AU)</MenuItem>
                        </Select>
                    </FormControl>
                </AccordionDetails>
            </Accordion>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    Visuals
                </AccordionSummary>
                <AccordionDetails>
                    <i>Decreasing the perspective factor will dramatically increase the size of stars! Scale them down first to preserve your framerate.</i><br /><br />
                    {StandardSlider({
                        name: "Perspective Factor",
                        value: perspectiveFactorValue,
                        min: 0,
                        max: 1,
                        handleChange: handleChange_pfactor,
                        valueLabelDisplay: "on",
                        step: .01
                    })}
                    {StandardSlider({
                        name: "Stars Scale",
                        value: starScaleValue,
                        min: 0,
                        max: 2,
                        handleChange: handleChange_starScale,
                        valueLabelDisplay: "on",
                        step: .001,
                        extraMarks: [1]
                    })}
                </AccordionDetails>
            </Accordion>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    Filter By
                </AccordionSummary>
                <AccordionDetails>
                    {StandardSlider({
                        name: "Distance",
                        ariaLabel: "Distance Range",
                        value: distRangeValue,
                        min: DIST_RANGE[0],
                        max: DIST_RANGE[1],
                        handleChange: handleChange_dist,
                        labelSuffix: " pc",
                        step: 1
                    })}
                </AccordionDetails>
            </Accordion>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                >
                    Color By
                </AccordionSummary>
                <AccordionDetails>
                    <FormControl fullWidth>
                        <InputLabel >Color</InputLabel>
                        <Select
                            value={colorByModeChosen}
                            label="Color"
                            onChange={handleChange_colorByMode}
                        >
                            <MenuItem value={ColorByModes.WHITE}>White</MenuItem>
                            <MenuItem value={ColorByModes.DISTANCE_HUES}>Distance (Hues)</MenuItem>
                            <MenuItem value={ColorByModes.DISTANCE_GRAYSCALE}>Distance (Grayscale)</MenuItem>
                            <MenuItem value={ColorByModes.ST_SPECTYPE}>Stellar Class (st_spectype)</MenuItem>
                        </Select>
                    </FormControl>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
};

/*
<List>
                {['Inbox', 'Starred', 'Send email', 'Drafts'].map((text, index) => (
                    <ListItem key={text} disablePadding>
                        <ListItemButton>
                            <ListItemIcon>
                                {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
                            </ListItemIcon>
                            <ListItemText primary={text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider />
            <List>
                {['All mail', 'Trash', 'Spam'].map((text, index) => (
                    <ListItem key={text} disablePadding>
                        <ListItemButton>
                            <ListItemIcon>
                                {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
                            </ListItemIcon>
                            <ListItemText primary={text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
*/

export default DrawerList;